import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { CheckCircle2, RotateCcw } from "lucide-react";

import {
  Button,
  DisclosureRow,
  EmptyState,
  Money,
  Pagination,
  SkeletonRows,
  Status,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
  cn,
  focusRing,
} from "../../../../components/ui";
import { shortDay } from "../../../../components/calendar/calendarDays";
import { financeAPI } from "../../../services/businessErpService";
import { AGENCY_NAME } from "../../hr/contributionsView";
import { holidayStatutoryText } from "../../hr/holidayPayView";
import PayrollCalculations from "./PayrollCalculations";
import PeriodDecisionDialog from "./PeriodDecisionDialog";
import { HISTORY_PATH, approvalStanding, describeReview, periodFacts, periodName, periodStatus } from "./payrollFinance";

const holidayDay = (date) => shortDay(String(date).slice(0, 10));

/** Holiday pay as Finance reads it: each day that pays something or that HR decided, read only. */
function HolidayDecisions({ holiday }) {
  if (!holiday?.lines?.length) return null;
  return (
    <section>
      <h4 className="text-label text-foreground">Holiday pay</h4>
      <ul aria-label="Holiday pay decisions" className="mt-1 divide-y divide-subtle">
        {holiday.lines.map((line) => (
          <li key={`${line.memberName}-${line.date}`} className="py-2">
            <p className="flex items-baseline justify-between gap-3 text-foreground">
              <span className="min-w-0">
                <span className="font-medium">{line.memberName}</span>
                {" — "}{line.name}, {holidayDay(line.date)} ({line.worked ? "worked" : "not worked"}{line.overtime ? ", with overtime" : ""})
              </span>
              <span className="shrink-0 whitespace-nowrap">
                {line.decided === "unresolved" ? <Status tone="danger" icon>Not decided</Status> : <Money value={line.amountCents} />}
              </span>
            </p>
            <p className="text-secondary">
              {line.decided === "reviewed"
                ? `Entered by ${line.reviewedByName || "HR"}: ${line.reason || "no reason recorded"}`
                : line.decided === "unresolved"
                  ? "HR has not entered an amount for this day."
                  : "Calculated from the official calendar and the holiday pay rules."}
            </p>
            {holidayStatutoryText(line.statutoryOutcome, line.statutoryEvidence) ? (
              <p className="text-meta text-secondary">{holidayStatutoryText(line.statutoryOutcome, line.statutoryEvidence)}</p>
            ) : null}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-meta text-tertiary">Payroll decides these. To change one, return the payroll to HR.</p>
    </section>
  );
}

const REVIEW_PAGE_SIZE = 3;

const contributionMonth = (month) => (month
  ? ` for ${new Date(`${month}T00:00:00Z`).toLocaleDateString("en-PH", { timeZone: "UTC", month: "long", year: "numeric" })}`
  : "");

/** The money Finance is asked to approve. Employer contributions are a business cost, never taken from net pay. */
function PeriodFigures({ period }) {
  const statutory = period.statutory;
  const figures = [
    ["Gross", period.gross_pay_cents],
    ["Employee deductions", period.deductions_cents],
    ["Net pay", period.net_pay_cents, true],
  ];
  if (statutory?.agencies?.length) figures.push(["Employer contributions", statutory.employerCents]);
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
      {figures.map(([label, cents, strong]) => (
        <div key={label} className="min-w-0">
          <dt className="text-meta text-secondary">{label}</dt>
          <dd>
            <Money value={cents} className={strong ? "text-metric text-foreground" : "text-money text-foreground"} />
          </dd>
        </div>
      ))}
      {Number(statutory?.withholding?.employeeCents || 0) > 0 ? (
        <p className="col-span-full text-meta text-tertiary">
          Employee deductions include <Money value={statutory.withholding.employeeCents} /> withholding tax, held from pay for the BIR.
        </p>
      ) : null}
    </dl>
  );
}

/** What a responsible decision needs that is not on the face of the period: read only. */
function ReviewDetails({ period }) {
  const statutory = period.statutory;
  const reviewed = statutory?.reviewed || [];
  return (
    <div className="space-y-4 pb-2 pt-1 text-body-sm">
      {reviewed.length ? (
        <section>
          <h4 className="text-label text-foreground">Amounts HR entered by review</h4>
          <ul aria-label="Amounts reviewed by HR" className="mt-1 divide-y divide-subtle">
            {reviewed.map((item) => (
              <li key={`${item.memberName}-${item.agency}`} className="py-2">
                <p className="text-foreground">
                  <span className="font-medium">{item.memberName}, {AGENCY_NAME[item.agency] || item.agency}</span>
                  {item.agency === "BIR_WHT"
                    ? <>{" — withheld from pay "}<Money value={item.employeeCents} />.</>
                    : <>{" — employee "}<Money value={item.employeeCents} />{", business "}<Money value={item.employerCents} />.</>}
                </p>
                <p className="text-secondary">Reason given{item.reviewedByName ? ` by ${item.reviewedByName}` : ""}: {item.reason}</p>
                {item.caseText ? <p className="text-meta text-tertiary">{item.caseText}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <HolidayDecisions holiday={period.holiday} />

      {statutory?.agencies?.length ? (
        <section>
          <h4 className="mb-1 text-label text-foreground">Government contributions{contributionMonth(statutory.month)}</h4>
          <Table caption="Contribution breakdown" className="max-w-md">
            <THead>
              <Tr>
                <Th>Agency</Th>
                <Th numeric>Employees</Th>
                <Th numeric>Business</Th>
              </Tr>
            </THead>
            <TBody>
              {statutory.agencies.map((row) => (
                <Tr key={row.agency}>
                  <Td className="font-medium">{AGENCY_NAME[row.agency] || row.agency}</Td>
                  <Td numeric><Money value={row.employeeCents} /></Td>
                  <Td numeric><Money value={row.employerCents} /></Td>
                </Tr>
              ))}
              <Tr>
                <Td className="font-semibold">Total</Td>
                <Td numeric className="font-semibold"><Money value={statutory.employeeCents} /></Td>
                <Td numeric className="font-semibold"><Money value={statutory.employerCents} /></Td>
              </Tr>
            </TBody>
          </Table>
          <p className="mt-2 text-meta text-tertiary">Payroll works these out. To change one, return the payroll to HR.</p>
        </section>
      ) : null}

      {period.reviews?.length ? (
        <section>
          <h4 className="text-label text-foreground">Earlier Finance decisions</h4>
          <ul className="mt-1 space-y-1 text-secondary">
            {period.reviews.map((review, index) => <li key={`${review.createdAt}-${index}`}>{describeReview(review)}</li>)}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/** One payroll HR submitted: which period, how much, what is unusual, what Finance decides. */
function ReviewObject({ period, canApprove, onDecide }) {
  const statutory = period.statutory;
  const reviewedCount = statutory?.reviewed?.length || 0;
  const unresolved = Number(statutory?.unresolvedCount || 0);
  const holidayReviewed = Number(period.holiday?.reviewedCount || 0);
  const lastReturn = period.reviews?.[0]?.action === "returned" ? period.reviews[0] : null;
  const headingId = `review-${period.id}`;
  // APP-HR-101: who prepared it, and whether this viewer may approve it. The server decides either way.
  const standing = approvalStanding(period);

  return (
    <article aria-labelledby={headingId} className="rounded-panel border border-default bg-surface">
      <div className="space-y-4 p-4 sm:p-5">
        <header>
          <h2 id={headingId} className="text-section-title text-foreground">{periodName(period)}</h2>
          <p className="mt-0.5 text-body-sm text-secondary">{periodFacts(period)}</p>
          {standing.prepared ? <p className="mt-0.5 text-body-sm text-secondary">{standing.prepared}</p> : null}
        </header>

        <PeriodFigures period={period} />

        {statutory?.agencies?.length ? (
          <p className="text-meta text-tertiary">Employer contributions are paid by the business on top of salaries, not deducted from them.</p>
        ) : null}

        {unresolved || reviewedCount || holidayReviewed || lastReturn ? (
          <ul className="space-y-1 text-body-sm">
            {unresolved ? (
              <li><Status tone="danger" icon>{unresolved} contribution or tax amount{unresolved === 1 ? " still needs" : "s still need"} a decision from HR</Status></li>
            ) : null}
            {reviewedCount ? (
              <li><Status tone="warning" icon>{reviewedCount} amount{reviewedCount === 1 ? "" : "s"} entered by HR review</Status></li>
            ) : null}
            {holidayReviewed ? (
              <li><Status tone="warning" icon>{holidayReviewed} holiday pay amount{holidayReviewed === 1 ? "" : "s"} entered by HR review</Status></li>
            ) : null}
            {lastReturn ? <li className="text-secondary">Resubmitted after: {describeReview(lastReturn)}</li> : null}
          </ul>
        ) : null}
      </div>

      <div className="border-t border-subtle px-4 sm:px-5">
        <PayrollCalculations periodId={period.id} />
      </div>

      <div className="border-t border-subtle px-4 sm:px-5">
        <DisclosureRow label="Review details" hint="Reviewed amounts, holiday pay, agency totals and earlier decisions">
          <ReviewDetails period={period} />
        </DisclosureRow>
      </div>

      <footer className="flex flex-col-reverse gap-2 border-t border-subtle px-4 py-3 sm:flex-row sm:items-center sm:justify-end sm:px-5">
        {canApprove ? (
          <>
            {standing.note ? <p className="text-body-sm text-secondary sm:mr-auto sm:max-w-[60ch]">{standing.note}</p> : null}
            <Button icon={RotateCcw} onClick={() => onDecide("return", period)} className="max-sm:w-full">Return</Button>
            {standing.mode ? (
              <Button variant="primary" icon={CheckCircle2} onClick={() => onDecide(standing.mode, period)} className="max-sm:w-full">
                {standing.mode === "approve_exception" ? "Approve as owner exception" : "Approve for release"}
              </Button>
            ) : null}
          </>
        ) : (
          <p className="text-body-sm text-secondary">Waiting for someone who can approve payroll</p>
        )}
      </footer>
    </article>
  );
}

/** To review — payroll HR submitted for a Finance decision. */
export default function PayrollToReview() {
  const { periods, loading, reload, setNotice, canApprove } = useOutletContext();
  const [decision, setDecision] = useState(null); // { mode, period }
  const [page, setPage] = useState(1);

  const waiting = periods.filter((row) => periodStatus(row) === "under_review");
  const withHr = periods.filter((row) => periodStatus(row) === "approved").length;
  const totalPages = Math.max(1, Math.ceil(waiting.length / REVIEW_PAGE_SIZE));
  const visibleWaiting = waiting.slice((page - 1) * REVIEW_PAGE_SIZE, page * REVIEW_PAGE_SIZE);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  if (loading) return <SkeletonRows rows={3} columns={4} label="Loading payroll to review" />;

  const submit = async (remarks, doneMessage, extra) => {
    const { mode, period } = decision;
    if (mode === "approve_exception") {
      await financeAPI.reviewPayrollPeriod(period.id, { remarks, owner_exception: true, exception_reason: extra?.exceptionReason });
    } else {
      const call = mode === "approve" ? financeAPI.reviewPayrollPeriod : financeAPI.returnPayrollPeriod;
      await call(period.id, { remarks });
    }
    setDecision(null);
    await reload();
    setNotice({ tone: "success", text: doneMessage });
  };

  return (
    <div className="space-y-4">
      {waiting.length ? (
        visibleWaiting.map((period) => (
          <ReviewObject key={period.id} period={period} canApprove={canApprove} onDecide={(mode, row) => setDecision({ mode, period: row })} />
        ))
      ) : (
        <div className="rounded-panel border border-default bg-surface">
          <EmptyState title="Nothing to review" description="HR hasn't sent any payroll for a Finance decision." />
        </div>
      )}

      {waiting.length ? (
        <div className="flex flex-col gap-2 border-t border-subtle sm:flex-row sm:items-center sm:justify-between">
          <span className="pt-3 text-meta text-secondary">Showing {(page - 1) * REVIEW_PAGE_SIZE + 1}-{Math.min(page * REVIEW_PAGE_SIZE, waiting.length)} of {waiting.length}</span>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} label="Payroll review pagination" />
        </div>
      ) : null}

      {withHr ? (
        <p className="text-body-sm text-secondary">
          {withHr} approved payroll{withHr === 1 ? " is" : "s are"} waiting for HR to release payslips.{" "}
          <Link to={HISTORY_PATH} className={cn("rounded-control font-medium text-accent hover:underline", focusRing)}>See History</Link>
        </p>
      ) : null}

      {decision ? (
        <PeriodDecisionDialog
          mode={decision.mode}
          period={decision.period}
          prepared={approvalStanding(decision.period).prepared}
          reasonMinLength={approvalStanding(decision.period).reasonMinLength}
          onClose={() => setDecision(null)}
          onSubmit={submit}
        />
      ) : null}
    </div>
  );
}
